import { useEffect, useRef, useState } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { apiGet } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';

interface OrgRow {
  id: string;
  slug: string;
  legal_name: string;
  trade_name: string | null;
  org_type: 'HQ' | 'BRANCH' | 'STANDALONE';
  parent_org_id: string | null;
}
interface AccessibleResp {
  home_org_id: string;
  items: OrgRow[];
}

export function OrgSwitcher() {
  const { user, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<AccessibleResp | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    apiGet<AccessibleResp>('/v1/auth/accessible-orgs').then(setData).catch(() => setData(null));
  }, [user?.org_id]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user || !data || data.items.length <= 1) return null;

  const current = data.items.find((o) => o.id === user.org_id);
  const isBranch = current?.org_type === 'BRANCH';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-0 px-3 py-1.5 text-xs font-medium text-neutral-800 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700"
      >
        <Building2 size={14} className="text-brand-600 dark:text-brand-300" />
        <span className="max-w-[160px] truncate">{current?.trade_name || current?.legal_name || user.org_slug}</span>
        {isBranch ? (
          <span className="rounded bg-warning-100 px-1 py-0.5 text-xxs uppercase tracking-wider text-warning-800 dark:bg-warning-900 dark:text-warning-100">filial</span>
        ) : (
          <span className="rounded bg-brand-100 px-1 py-0.5 text-xxs uppercase tracking-wider text-brand-800 dark:bg-brand-900 dark:text-brand-100">matriz</span>
        )}
        <ChevronDown size={12} />
      </button>
      {open ? (
        <div className="absolute right-0 z-50 mt-1 w-72 overflow-hidden rounded-md border border-neutral-200 bg-neutral-0 shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
          <div className="border-b border-neutral-100 px-3 py-2 text-xxs uppercase tracking-wider text-neutral-500 dark:border-neutral-700">Trocar organização</div>
          <ul className="max-h-80 overflow-y-auto">
            {data.items.map((o) => {
              const isCur = o.id === user.org_id;
              return (
                <li key={o.id}>
                  <button
                    type="button"
                    disabled={isCur || busy === o.id}
                    onClick={async () => {
                      setBusy(o.id);
                      try {
                        await switchOrg(o.id);
                        setOpen(false);
                      } catch (e) {
                        alert(`Falha ao trocar: ${(e as Error).message}`);
                      } finally {
                        setBusy(null);
                      }
                    }}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-neutral-50 disabled:cursor-not-allowed dark:hover:bg-neutral-700 ${isCur ? 'bg-neutral-50 dark:bg-neutral-700/50' : ''}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-neutral-800 dark:text-neutral-100">{o.trade_name || o.legal_name}</span>
                        <span className={`shrink-0 rounded px-1 py-0.5 text-xxs uppercase tracking-wider ${o.org_type === 'HQ' ? 'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-100' : 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-100'}`}>
                          {o.org_type === 'HQ' ? 'matriz' : 'filial'}
                        </span>
                      </div>
                      <div className="truncate text-xxs text-neutral-500">{o.slug}</div>
                    </div>
                    {isCur ? <Check size={14} className="shrink-0 text-success-600" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
